import React, { useEffect, useState, useCallback } from 'react';
import { Image, Edit, Trash2, Plus, Eye, EyeOff, ArrowUp, ArrowDown, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { useDropzone } from 'react-dropzone';

const ImageUploader = ({ onImageUploaded, currentImage }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  
  const token = localStorage.getItem('adminToken');

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = `${API}${response.data.url}`.replace('/api/api', '/api');
      onImageUploaded(imageUrl);
      toast.success('Bild hochgeladen!');
    } catch (error) {
      toast.error('Fehler beim Hochladen');
      setPreview(currentImage);
    } finally {
      setUploading(false);
    }
  }, [token, onImageUploaded, currentImage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.gif', '.avif']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  return (
    <div>
      <Label>Bild</Label>
      <div
        {...getRootProps()}
        className={`mt-2 border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
          isDragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
        }`}
      >
        <input {...getInputProps()} />
        
        {preview ? (
          <div className="relative">
            <img 
              src={preview} 
              alt="Vorschau" 
              className="w-full h-48 object-cover rounded-lg"
            />
            {uploading && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPreview(null);
                onImageUploaded('');
              }}
              className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="py-8">
            <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            {isDragActive ? (
              <p className="text-blue-600 font-medium">Bild hier ablegen...</p>
            ) : (
              <>
                <p className="text-slate-600 font-medium">Bild hierher ziehen</p>
                <p className="text-slate-400 text-sm mt-1">oder klicken zum Auswählen</p>
                <p className="text-slate-400 text-xs mt-2">JPEG, PNG, WebP, AVIF (max. 10MB)</p>
              </>
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-2">Empfohlen: 1920x1080px, Querformat (16:9)</p>
    </div>
  );
};

const AdminSlider = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlide, setEditingSlide] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formImageUrl, setFormImageUrl] = useState('');

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSlides = () => {
    axios.get(`${API}/slider?active_only=false`)
      .then(res => setSlides(res.data))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  const openDialog = (slide = null) => {
    setEditingSlide(slide);
    setFormImageUrl(slide?.image_url || '');
    setIsDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    data.order = parseInt(data.order) || 0;
    data.active = data.active === 'on';
    data.image_url = formImageUrl;

    if (!data.image_url) {
      toast.error('Bitte laden Sie ein Bild hoch');
      return;
    }

    try {
      if (editingSlide) {
        await axios.put(`${API}/slider/${editingSlide.id}`, data, { headers });
        toast.success('Slider-Bild aktualisiert!');
      } else {
        await axios.post(`${API}/slider`, data, { headers });
        toast.success('Slider-Bild hinzugefügt!');
      }
      setIsDialogOpen(false);
      setEditingSlide(null);
      setFormImageUrl('');
      fetchSlides();
    } catch (error) {
      console.error('Slider save error:', error.response?.data || error.message);
      toast.error('Fehler beim Speichern: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchtest du dieses Slider-Bild wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/slider/${id}`, { headers });
      toast.success('Slider-Bild gelöscht!');
      fetchSlides();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleActive = async (slide) => {
    try {
      await axios.put(`${API}/slider/${slide.id}`, { active: !slide.active }, { headers });
      toast.success(slide.active ? 'Slider-Bild deaktiviert' : 'Slider-Bild aktiviert');
      fetchSlides();
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const changeOrder = async (slide, direction) => {
    const newOrder = direction === 'up' ? slide.order - 1 : slide.order + 1;
    try {
      await axios.put(`${API}/slider/${slide.id}`, { order: newOrder }, { headers });
      fetchSlides();
    } catch (error) {
      toast.error('Fehler beim Sortieren');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  return (
    <div data-testid="admin-slider">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Hero Slider</h1>
          <p className="text-slate-600">Verwalte die Werbe-Bilder auf der Startseite.</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { 
          if (!open) { 
            setIsDialogOpen(false);
            setEditingSlide(null); 
            setFormImageUrl(''); 
          } 
        }}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog(null)} data-testid="add-slide-btn">
              <Plus className="w-4 h-4 mr-2" /> Neues Bild
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSlide ? 'Slider-Bild bearbeiten' : 'Neues Slider-Bild'}</DialogTitle>
            </DialogHeader>
            {isDialogOpen && (
            <form onSubmit={handleSave} className="space-y-4" key={editingSlide?.id || 'new'}>
              {/* Image Upload with Drag & Drop */}
              <ImageUploader 
                onImageUploaded={setFormImageUrl} 
                currentImage={formImageUrl}
              />
              
              <div>
                <Label htmlFor="title">Titel (Überschrift)</Label>
                <Input id="title" name="title" defaultValue={editingSlide?.title} required />
              </div>
              <div>
                <Label htmlFor="subtitle">Untertitel</Label>
                <Input id="subtitle" name="subtitle" defaultValue={editingSlide?.subtitle} />
              </div>
              <div>
                <Label htmlFor="video_url">YouTube Video URL (optional)</Label>
                <Input 
                  id="video_url" 
                  name="video_url" 
                  defaultValue={editingSlide?.video_url} 
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <p className="text-xs text-slate-500 mt-1">Falls gesetzt, wird ein "Video ansehen" Button angezeigt</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="link_url">Link URL</Label>
                  <Input id="link_url" name="link_url" defaultValue={editingSlide?.link_url} placeholder="/kontakt" />
                </div>
                <div>
                  <Label htmlFor="link_text">Button Text</Label>
                  <Input id="link_text" name="link_text" defaultValue={editingSlide?.link_text} placeholder="Jetzt anmelden" />
                </div>
              </div>
              <div>
                <Label htmlFor="order">Reihenfolge</Label>
                <Input id="order" name="order" type="number" defaultValue={editingSlide?.order || slides.length + 1} />
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="active" 
                  name="active" 
                  defaultChecked={editingSlide?.active ?? true}
                  className="rounded"
                />
                <Label htmlFor="active">Aktiv (sichtbar auf der Startseite)</Label>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit">Speichern</Button>
              </div>
            </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Preview Info */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-sm text-blue-800">
            <strong>Tipp:</strong> Ziehen Sie Bilder einfach per Drag & Drop in das Upload-Feld. 
            Für beste Ergebnisse verwenden Sie Bilder im Querformat (16:9) mit einer Mindestauflösung von 1920x1080 Pixeln.
          </p>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {slides.sort((a, b) => a.order - b.order).map((slide, index) => (
          <Card key={slide.id} className={!slide.active ? 'opacity-60' : ''} data-testid={`slide-item-${slide.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                {/* Thumbnail */}
                <div className="relative w-40 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                  <img 
                    src={slide.image_url} 
                    alt={slide.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/160x96/3b82f6/ffffff?text=Bild';
                    }}
                  />
                  {!slide.active && (
                    <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center">
                      <span className="text-white text-xs font-medium">Inaktiv</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">{slide.title}</h3>
                  {slide.subtitle && (
                    <p className="text-sm text-slate-600 truncate">{slide.subtitle}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    {slide.link_url && (
                      <span className="text-blue-600">{slide.link_text || 'Link'} → {slide.link_url}</span>
                    )}
                    <span className="text-slate-400">Reihenfolge: {slide.order}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => changeOrder(slide, 'up')}
                    disabled={index === 0}
                    title="Nach oben"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => changeOrder(slide, 'down')}
                    disabled={index === slides.length - 1}
                    title="Nach unten"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => toggleActive(slide)}
                    title={slide.active ? 'Deaktivieren' : 'Aktivieren'}
                  >
                    {slide.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => openDialog(slide)}
                    title="Bearbeiten"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDelete(slide.id)}
                    className="text-red-600 hover:text-red-700"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {slides.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Image className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>Noch keine Slider-Bilder vorhanden.</p>
            <p className="text-sm mt-2">Füge dein erstes Werbe-Bild hinzu!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSlider;
